import React, { useEffect, useState } from "react";
import { Input, Table, message } from "antd";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { MonitorPlay, Search } from "lucide-react";
import { logOutUser } from "../../../common";
import { AUTH_TOKEN, REACT_APP_PAM_API, ORG_ID, DOMAIN_ID } from "../../../constants";
import { useDebounce } from "../../../common/debounce";
import {
  FilterCard,
  FilterField,
  RowActionMenu,
  TableCard,
  anTableProps,
  tableEmpty,
} from "../../../design-system/tableUi";
import { AN_COLORS } from "../../../design-system/tokens";
import { SourceTag } from "./setupScan";

const PAGE_SIZE = 10;

// The endpoint details page's "Local users" for non-administrators: the local
// accounts on one endpoint (server-paginated listAllEpmUsers). Administrators
// get LocalUsersTable, with logins, rotation and added users.
const EndpointDetailsUser = ({ stateHostAddress }) => {
  const location = useLocation();
  const [notification, notificationContext] = message.useMessage();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);
  const [pageIndex, setPageIndex] = useState(1); // 1-indexed
  const [totalCount, setTotalCount] = useState(0);
  const [epmUsers, setEpmUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const { instanceId } = location.state || {};
  const endpointId = Number(stateHostAddress?.instanceId || instanceId) || 0;

  // A new search starts from the first page.
  useEffect(() => {
    setPageIndex(1);
  }, [debouncedSearch]);

  useEffect(() => {
    let live = true;
    const payload = {
      domainId: DOMAIN_ID(),
      pageId: pageIndex,
      pageSize: PAGE_SIZE,
      token: AUTH_TOKEN(),
      instanceIds: [endpointId],
      filter: { filterBy: "", value: "" },
      search: debouncedSearch,
      orgId: ORG_ID(),
    };
    setIsLoading(true);
    axios
      .post(`${REACT_APP_PAM_API}/endpointUsers/listAllEpmUsers`, payload, {
        headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() },
      })
      .then((res) => {
        if (!live) return;
        setIsLoading(false);
        const rows = (res?.data?.epmUsers || []).map((u, i) => ({ ...u, id: i + 1 }));
        setEpmUsers(rows);
        // Some responses carry no totalCount; never show fewer than the rows on screen.
        setTotalCount(Math.max(Number(res?.data?.totalCount) || 0, rows.length));
      })
      .catch((err) => {
        if (!live) return;
        setIsLoading(false);
        if (err?.response?.data?.message === "Invalid credentials" || err?.response?.status === 401) {
          logOutUser();
        } else {
          notification.error({ content: "Unable to fetch endpoint users" });
        }
      });
    return () => {
      live = false;
    };
  }, [endpointId, pageIndex, debouncedSearch, notification]);

  const columns = [
    {
      title: "User",
      key: "userName",
      render: (_, u) => (
        <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
          <span style={{ fontWeight: 500, color: AN_COLORS.ink }} title={u.userName}>
            {u.userName || "—"}
          </span>
          {/* "gateway" = found by the scan, "manual" = typed in; agent-reported
              accounts carry no tag. */}
          {(u.source === "gateway" || u.source === "manual") && <SourceTag source={u.source} />}
        </div>
      ),
    },
    {
      title: "",
      key: "action",
      width: 56,
      align: "center",
      render: (_, u) => (
        <RowActionMenu
          items={[
            {
              key: "session-recordings",
              icon: <MonitorPlay size={13} />,
              label: (
                <Link to={`/pam/jumpServer-sessionRecordings?epmUser=${encodeURIComponent(u.userName || "")}`}>
                  View session recordings
                </Link>
              ),
            },
          ]}
        />
      ),
    },
  ];

  const applied = debouncedSearch
    ? [{ key: "search", label: `Search: ${debouncedSearch}`, clear: () => setSearch("") }]
    : [];

  return (
    <div>
      {notificationContext}
      <FilterCard
        columns="minmax(0, 360px)"
        applied={applied}
        onClearAll={() => setSearch("")}
        showing={epmUsers.length}
        total={totalCount}
      >
        <FilterField label="Search user">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            prefix={<Search size={14} color={AN_COLORS.muted} />}
            placeholder="ubuntu"
            style={{ height: 36 }}
          />
        </FilterField>
      </FilterCard>

      <TableCard>
        <Table
          {...anTableProps}
          columns={columns}
          dataSource={epmUsers}
          rowKey={(u) => `${u.userId ?? ""}-${u.userName}-${u.id}`}
          loading={isLoading}
          scroll={{ x: "max-content" }}
          locale={tableEmpty(isLoading, debouncedSearch ? "No local users match your search." : "No local users found.")}
          pagination={{
            current: pageIndex,
            pageSize: PAGE_SIZE,
            total: totalCount,
            showSizeChanger: false,
            onChange: setPageIndex,
          }}
        />
      </TableCard>
    </div>
  );
};

export default EndpointDetailsUser;
